import { useState, useRef, useEffect } from 'react';
import { Play, Pause, Volume2, AlertCircle } from 'lucide-react';

const AudioPlayer = ({ url, primaryColor }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [error, setError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const audioRef = useRef(null);

  // Validar URL
  useEffect(() => {
    // Verificar se a URL parece válida
    if (!url || typeof url !== 'string' || !url.match(/^https?:\/\/.+\.mp3/i)) {
      console.error('URL de áudio inválida:', url);
      setError(true);
      setIsLoading(false);
      return;
    }

    // Limpar estados
    setError(false);
    setIsLoading(true);
  }, [url]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    
    const handleTimeUpdate = () => {
      if (audio.duration) {
        setProgress((audio.currentTime / audio.duration) * 100);
      }
    };
    
    const handleLoadedMetadata = () => {
      setDuration(audio.duration);
      setIsLoading(false);
    };
    
    const handleEnded = () => {
      setIsPlaying(false);
      setProgress(0);
    };
    
    const handleError = (e) => {
      console.error('Erro ao carregar áudio:', e);
      setError(true);
      setIsLoading(false);
    };
    
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('error', handleError);
    
    // Tenta pré-carregar para verificar se o áudio existe
    audio.load();
    
    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('error', handleError);
    };
  }, [url]);

  const togglePlay = () => {
    if (error) return;
    
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      const playPromise = audioRef.current.play();
      
      // Handle play promise to catch autoplay restrictions
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            // Autoplay started
          })
          .catch(err => {
            console.error('Erro ao iniciar reprodução:', err);
            setIsPlaying(false);
          });
      }
    }
    setIsPlaying(!isPlaying);
  };

  const handleProgressClick = (e) => {
    if (error) return;
    
    const progressBar = e.currentTarget;
    const clickPosition = (e.clientX - progressBar.getBoundingClientRect().left);
    const progressBarWidth = progressBar.offsetWidth;
    const newProgress = (clickPosition / progressBarWidth) * 100;
    
    // Atualizar a posição do áudio
    if (audioRef.current.duration) {
      audioRef.current.currentTime = (newProgress / 100) * audioRef.current.duration;
    }
  };

  const formatTime = (seconds) => {
    if (isNaN(seconds)) return '0:00';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}:${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`;
  };

  // Renderizar componente de erro ou carregamento, se necessário
  if (error) {
    return (
      <div className="mt-3 mb-1 rounded-lg bg-red-50 p-3 text-red-600 flex items-center">
        <AlertCircle className="w-5 h-5 mr-2" />
        <span className="text-sm">Não foi possível carregar o áudio.</span>
      </div>
    );
  }

  return (
    <div className="mt-3 mb-1 rounded-lg overflow-hidden bg-gray-100 p-3">
      <audio ref={audioRef} src={url} preload="metadata" />
      
      <div className="flex items-center space-x-3">
        <button 
          onClick={togglePlay}
          className={`p-2 rounded-full flex-shrink-0 text-white ${isLoading ? 'opacity-50' : ''}`}
          style={{ backgroundColor: primaryColor || '#4a6cf7' }}
          disabled={isLoading}
        >
          {isLoading ? (
            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : isPlaying ? (
            <Pause className="w-5 h-5" />
          ) : (
            <Play className="w-5 h-5" />
          )}
        </button>
        
        <div className="flex-1">
          <div 
            className="h-2 bg-gray-300 rounded-full cursor-pointer relative overflow-hidden" 
            onClick={handleProgressClick}
          >
            <div 
              className="h-full absolute left-0 top-0 rounded-full" 
              style={{ 
                width: `${progress}%`,
                backgroundColor: primaryColor || '#4a6cf7'
              }}
            ></div>
          </div>
          
          <div className="flex justify-between mt-1 text-xs text-gray-600">
            <span>{formatTime(audioRef.current?.currentTime || 0)}</span>
            <span>{formatTime(duration)}</span>
          </div>
        </div>
        
        <div className="text-gray-500 flex-shrink-0">
          <Volume2 className="w-5 h-5" />
        </div>
      </div>
    </div>
  );
};

export default AudioPlayer;